import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useRef, useCallback, useState, ReactNode } from 'react';

import CloseIcon from '@/fo/icons/base/icons/close10.svg';
import PhotoIcon from '@/fo/icons/base/icons/photo.svg';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';

import { styles } from './UiKitInputPhoto.styles';

export const SUPPORTED_IMAGE_FORMATS = 'JPG, GIF, JPEG, PNG, BMP';

export interface PhotoFile {
  filename: string;
  previewUrl: string;
}

interface UiKitInputPhotoSingleProps {
  value?: PhotoFile | null;
  onSelect?: (file: File | null) => void;
  multiple?: false;
  onRemove?: undefined;
  maxCount?: undefined;
}

interface UiKitInputPhotoMultipleProps {
  value?: PhotoFile[] | null;
  onSelect?: (file: File[] | null) => void;
  multiple: true;
  onRemove?: (file: PhotoFile) => void;
  maxCount?: number;
}

interface UiKitInputPhotoBaseProps {
  // Максимальный размер файла, указывается в байтах
  maxSize?: number;
  loading?: boolean;
  title?: ReactNode;
  helperText?: ReactNode;
  note?: ReactNode;
}

export type UiKitInputPhotoProps = (
  | UiKitInputPhotoSingleProps
  | UiKitInputPhotoMultipleProps
) &
  UiKitInputPhotoBaseProps;

export function UiKitInputPhoto({
  maxSize = 1024 * 1024, // 1MB
  value,
  loading = false,
  title,
  helperText,
  note,
  multiple,
  onSelect,
  onRemove,
  maxCount
}: UiKitInputPhotoProps) {
  const slots = styles();

  const t = useTranslations();

  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize) {
        return `${t('The file size exceeds')} ${Math.round(maxSize / 1024 / 1024)}MB. ${t('Please upload a smaller snapshot')}`;
      }

      if (!file.type.startsWith('image/')) {
        return `${t('Unsuitable file format')}. ${t('Please upload a picture')} ${SUPPORTED_IMAGE_FORMATS}.`;
      }

      return null;
    },
    [maxSize, t]
  );

  const onFileSelect = useCallback(
    (file: File) => {
      if (multiple) {
        return;
      }

      const error = validateFile(file);
      if (error) {
        setError(error);
        return;
      }

      setError(null);
      onSelect?.(file);
    },
    [multiple, validateFile, onSelect]
  );

  const onFilesSelect = (files: File[]) => {
    if (!multiple) {
      return;
    }

    if (maxCount && files.length + (value?.length ?? 0) > maxCount) {
      setError(`${t('The number of images exceeds')} ${maxCount}.`);
      return;
    }

    const errors = files.map((file) => validateFile(file)).filter(Boolean);

    if (errors.length > 0) {
      setError(errors[0]);
      return;
    }

    setError(null);
    onSelect?.(files);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (multiple) {
      onFilesSelect(Array.from(event.target.files || []));
      return;
    }

    const file = event.target.files?.[0];

    if (file) {
      onFileSelect(file);
    }
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (loading) return;

      const file = event.dataTransfer.files[0];
      if (file) {
        onFileSelect(file);
      }
    },
    [onFileSelect, loading]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const onInputClick = useCallback(() => {
    if (!loading) {
      inputRef.current?.click();
    }
  }, [loading]);

  const onRemoveButtonClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onSelect?.(null);
    },
    [onSelect]
  );

  const onRemoveFileByFilename = (file: PhotoFile) => {
    if (!multiple) {
      return;
    }

    onRemove?.(file);
  };

  return (
    <div className={slots.base()}>
      <div className={slots.top()}>
        {title && <p className={slots.title()}>{title}</p>}
        {helperText && <p className={slots.text()}>{helperText}</p>}
      </div>
      <div
        className={slots.input()}
        onClick={onInputClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept=".jpg,.jpeg,.png,.bmp,.gif"
          onChange={onInputChange}
          className="hidden"
        />
        {(!value || multiple) && (
          <p className={slots.placeholder()}>
            <PhotoIcon />
          </p>
        )}
        {value && !multiple && (
          <div className={slots.preview()}>
            <Image
              className={slots.image()}
              src={value.previewUrl}
              alt="Preview"
              width={80}
              height={80}
            />
            <button
              className={slots.remove()}
              onClick={onRemoveButtonClick}
            >
              <CloseIcon />
            </button>
          </div>
        )}
      </div>
      {error && <p className={slots.error()}>{error}</p>}
      {multiple && !!value?.length && (
        <UiKitCarouselBase
          showArrows={false}
          loop={false}
          className={slots.multiplePreview()}
        >
          {value.map((file) => (
            <div
              key={file.previewUrl}
              className={slots.multiplePreviewItem()}
            >
              <Image
                src={file.previewUrl}
                alt="Preview"
                className={slots.image()}
                width={80}
                height={80}
              />
              <button
                className={slots.remove()}
                onClick={() => onRemoveFileByFilename(file)}
              >
                <CloseIcon />
              </button>
            </div>
          ))}
        </UiKitCarouselBase>
      )}
      {note && <p className={slots.note()}>{note}</p>}
    </div>
  );
}
